import React, { useEffect, useState, useRef } from "react";
import {
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  SelectChangeEvent,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import "./styles/_index.scss";
import CloseIcon from "@mui/icons-material/Close";
import ArrowCircleLeftIcon from "@mui/icons-material/ArrowCircleLeft";
import ArrowCircleRightIcon from "@mui/icons-material/ArrowCircleRight";
import axios from "axios";
import markerImage from "../../assets/vessel.png";

interface CustomTimelineProps {
  trackingData: any;
  videoConfig: any;
  currentFrame: number;
  onFrameClick: (frame: number) => void;
  videoRef: React.RefObject<HTMLVideoElement>;
  setIsGeneratingThumbnails?: (generating: boolean) => void;
}

interface TrackPoint {
  x: number;
  y: number;
}

interface TrackPath {
  trackPoints: TrackPoint[];
  connectingLines: string;
}

interface ScalingInfo {
  needsScaling: boolean;
  scale: number;
  offsetX: number;
  offsetY: number;
}
// video frames seprate, seprate section, show frame image below
const CustomTimeline: React.FC<CustomTimelineProps> = ({
  trackingData,
  videoConfig,
  currentFrame,
  onFrameClick,
  videoRef,
  setIsGeneratingThumbnails,
}) => {
  const [frameThumbnails, setFrameThumbnails] = useState<string[]>([]);
  const [thumbnailsLoaded, setThumbnailsLoaded] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const videoUrlRef = useRef<string | null>(null);
  const thumbnailsGeneratedRef = useRef<boolean>(false);
  const cachedThumbnailsRef = useRef<string[]>([]);
  //to make sure video is there then generate video
  React.useEffect(() => {
    const currentVideoSrc = videoRef.current?.src || null;
    if (currentVideoSrc !== videoUrlRef.current) {
      thumbnailsGeneratedRef.current = false;
      cachedThumbnailsRef.current = [];
      setThumbnailsLoaded(false);
      setFrameThumbnails([]);
      videoUrlRef.current = currentVideoSrc;
    }
  }, [videoRef.current?.src]);
  //ex: if we have 4 image, to generate thumnail
  React.useEffect(() => {
    const generateThumbnails = async () => {
      if (
        thumbnailsGeneratedRef.current &&
        cachedThumbnailsRef.current.length > 0
      ) {
        setFrameThumbnails(cachedThumbnailsRef.current);
        setThumbnailsLoaded(true);
        return;
      }
      if (
        !videoRef.current ||
        !canvasRef.current ||
        !trackingData ||
        !videoConfig
      )
        return;

      const video = videoRef.current;
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");

      if (
        !ctx ||
        video.readyState < 3 ||
        video.videoWidth === 0 ||
        video.videoHeight === 0
      )
        return;

      const forecastIds = Object.keys(trackingData);
      const forecastId = localStorage.fid;

      if (!forecastIds.includes(forecastId)) return;

      const forecastData = trackingData[forecastId];
      if (
        !forecastData ||
        !forecastData.lats ||
        !forecastData.lons ||
        forecastData.lats.length === 0
      )
        return;

      const totalFrames =
        videoConfig.corrected_frames || videoConfig.nr_of_frames || 7;

      if (setIsGeneratingThumbnails) setIsGeneratingThumbnails(true);

      const thumbnails: string[] = [];
      const originalTime = video.currentTime;
      const originalPaused = video.paused;

      const aspectRatio = video.videoWidth / video.videoHeight;
      canvas.width = 200;
      canvas.height = Math.round(200 / aspectRatio);

      try {
        if (!video.paused) video.pause();

        for (let frameIndex = 0; frameIndex < totalFrames; frameIndex++) {
          const targetTime = (frameIndex / (totalFrames - 1)) * video.duration;
          video.currentTime = targetTime;

          await new Promise<void>((resolve) => {
            const onSeeked = () => {
              video.removeEventListener("seeked", onSeeked);
              setTimeout(resolve, 50);
            };
            video.addEventListener("seeked", onSeeked);
            setTimeout(resolve, 500);
          });

          ctx.fillStyle = "#000000";
          ctx.fillRect(0, 0, canvas.width, canvas.height);

          try {
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          } catch (drawError) {
            ctx.fillStyle = "#333333";
            ctx.fillRect(0, 0, canvas.width, canvas.height);
          }

          thumbnails.push(canvas.toDataURL("image/jpeg", 0.85));
        }

        video.currentTime = originalTime;
        if (!originalPaused) video.play().catch(() => {});

        cachedThumbnailsRef.current = thumbnails;
        thumbnailsGeneratedRef.current = true;
        setFrameThumbnails(thumbnails);
        setThumbnailsLoaded(true);
      } catch (error) {
        console.error("Error generating thumbnails:", error);
      } finally {
        if (setIsGeneratingThumbnails) setIsGeneratingThumbnails(false);
      }
    };

    if (videoRef.current && trackingData && videoConfig) {
      const video = videoRef.current;
      const checkAndGenerate = () => {
        if (
          video.readyState >= 3 &&
          video.videoWidth > 0 &&
          video.videoHeight > 0
        ) {
          setTimeout(generateThumbnails, 1000);
        } else {
          setTimeout(checkAndGenerate, 200);
        }
      };
      if (video.readyState >= 3) {
        checkAndGenerate();
      } else {
        video.addEventListener("canplaythrough", checkAndGenerate, {
          once: true,
        });
      }
    }
  }, [videoRef, trackingData, videoConfig]);

  if (!trackingData || !videoConfig) return null;
  //if there is video but no tracking data it will show null not generate thumnail
  const forecastIds = Object.keys(trackingData);
  const forecastId = localStorage.fid;
  if (!forecastIds.includes(forecastId)) return null;

  const forecastData = trackingData[forecastId];
  if (
    !forecastData ||
    !forecastData.lats ||
    !forecastData.lons ||
    forecastData.lats.length === 0
  )
    return null;

  const totalFrames =
    videoConfig.corrected_frames || videoConfig.nr_of_frames || 7;

  const frameSegments = [];
  for (let i = 0; i < totalFrames; i++) {
    const time =
      forecastData.datetimes?.[i] ||
      `T+${Math.round(
        i * (videoConfig.timeinterval ? videoConfig.timeinterval + 1 : 4)
      )}h`;
    frameSegments.push({ frame: i, time, thumbnail: frameThumbnails[i] });
  }
  //video frame click
  const handleFrameClick = (frameNumber: number): void => {
    onFrameClick(frameNumber);
    if (videoRef.current?.duration) {
      const targetTime =
        (frameNumber / (totalFrames - 1)) * videoRef.current.duration;
      videoRef.current.currentTime = targetTime;
    }
  };

  return (
    <div className="custom-timeline">
      <canvas ref={canvasRef} className="timeline-canvas" />
      <div className="timeline-header">
        Vessel Track Timeline - Frame {Math.round(currentFrame) + 1}/
        {totalFrames}
      </div>
      {!thumbnailsLoaded ? (
        <div className="timeline-loading">
          <div className="loading-content">
            <div className="spinner" />
            Generating Frame Previews...
          </div>
        </div>
      ) : (
        <div className="timeline-track">
          {frameSegments.map((frameData, index) => {
            const isActive = Math.round(currentFrame) === frameData.frame;
            return (
              <div
                key={index}
                onClick={() => handleFrameClick(frameData.frame)}
                className={`frame-segment ${isActive ? "active" : ""}`}
                style={{ width: `${100 / totalFrames}%` }}
              >
                {frameData.thumbnail && (
                  <div
                    className="frame-thumbnail"
                    style={{ backgroundImage: `url(${frameData.thumbnail})` }}
                  />
                )}
                <div className="frame-number">{frameData.frame + 1}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const Charts: React.FC = () => {
  const [selectedRegion, setSelectedRegion] = useState<string>("");
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [selectedType, setSelectedType] = useState<string>("Wind");
  const [loadingLocation, setLoadingLocation] = useState<boolean>(true);
  const [loadingFiles, setLoadingFiles] = useState<boolean>(false);
  const [videoConfig, setVideoConfig] = useState<any>(null);
  const [trackingData, setTrackingData] = useState<any>(null);
  const [currentFrame, setCurrentFrame] = useState<number>(0);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [isFullscreenMode, setIsFullscreenMode] = useState<boolean>(false);
  const [forceRender, setForceRender] = useState<number>(0);
  const [isFullView, setIsFullView] = useState<boolean>(false);
  const [isGeneratingThumbnails, setIsGeneratingThumbnails] =
    useState<boolean>(false);
  const [initTimeMatched, setInitTimeMatched] = useState<boolean>(true);
  const [videoReady, setVideoReady] = useState<boolean>(false);
  const [videoError, setVideoError] = useState<string>("");
  const [showImagesPopup, setShowImagesPopup] = useState<boolean>(false);
  const [images, setImages] = useState<{ title: string; data: string }[]>([]);
  const [currentImageIndex, setCurrentImageIndex] = useState<number>(0);
  const [loadingImages, setLoadingImages] = useState<boolean>(false);
  const [projectLocation, setProjectLocation] = useState<{
    latitude: number;
    longitude: number;
  } | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const animationRef = useRef<number>();
  const loadAttemptRef = useRef<number>(0);

  const [trimmedProject, setTrimmedProject] = useState("");

  useEffect(() => {
    const project = localStorage.getItem("project");
    const trimmed = project
    ? project.match(/^(.*?)(?=\s*-\s*\d+)/)?.[0].trim() || ""
    : "";
    setTrimmedProject(trimmed);
  }, []);

  const latLonToPixel = (
    lat: number,
    lon: number,
    cfg: any,
    videoHeight: number
  ): TrackPoint => {
    if (!cfg) return { x: 0, y: 0 }; // if there is no cofig file it will return 0
    const normX = (lon - cfg.minlon) / (cfg.maxlon - cfg.minlon); //CFG video config data,
    const normY = (cfg.maxlat - lat) / (cfg.maxlat - cfg.minlat);

    const x = cfg.plot_left_pix + normX * cfg.plot_width_pix; // to get X value

    const totalUsed = cfg.plot_bottom_pix + cfg.plot_height_pix; //Y value is differ its start or bottom(based where to start)
    const remainingSpace = videoHeight - totalUsed; // bottom pixel + height got total (that pixel actual video pixel)
    const isFromBottom = remainingSpace < 120; // based on that check below 120 bottom above 120 top

    let y;
    if (isFromBottom) {
      //from bottom actual video sub get video value,
      const plotTopY = videoHeight - cfg.plot_bottom_pix - cfg.plot_height_pix;
      y = plotTopY + normY * cfg.plot_height_pix;
    } else {
      y = cfg.plot_bottom_pix + normY * cfg.plot_height_pix;
    }

    return { x, y };
  };
  //with in boutaries or not to check
  const isLatLonInBounds = (lat: number, lon: number, config: any): boolean => {
    if (!config) return false;
    return (
      lat >= config.minlat &&
      lat <= config.maxlat &&
      lon >= config.minlon &&
      lon <= config.maxlon
    );
  };
  //plot data with in bountaries,if lat and lon out of bountaries it will take out
  const isPointInBounds = (
    p: TrackPoint,
    cfg: any,
    videoHeight: number
  ): boolean => {
    if (!cfg) return false;

    const minX = cfg.plot_left_pix;
    const maxX = cfg.plot_left_pix + cfg.plot_width_pix;

    const totalUsed = cfg.plot_bottom_pix + cfg.plot_height_pix;
    const remainingSpace = videoHeight - totalUsed;
    const isFromBottom = remainingSpace < 120;

    let minY, maxY;
    if (isFromBottom) {
      const plotTopY = videoHeight - cfg.plot_bottom_pix - cfg.plot_height_pix;
      minY = plotTopY;
      maxY = plotTopY + cfg.plot_height_pix;
    } else {
      minY = cfg.plot_bottom_pix;
      maxY = cfg.plot_bottom_pix + cfg.plot_height_pix;
    }

    return p.x >= minX && p.x <= maxX && p.y >= minY && p.y <= maxY;
  };
  //create tracking path
  const createTrackPath = (
    forecastData: any,
    config: any,
    scalingInfo: ScalingInfo | null = null,
    videoHeight: number
  ): TrackPath => {
    if (!forecastData?.lats || !forecastData?.lons)
      return { trackPoints: [], connectingLines: "" };

    let trackPoints: TrackPoint[] = [];
    let connectingLinesData = "";
    let validPoints: Array<{ pixel: TrackPoint; index: number }> = [];

    for (let i = 0; i < forecastData.lats.length; i++) {
      const lat = forecastData.lats[i];
      const lon = forecastData.lons[i];
      if (
        lat !== undefined &&
        lon !== undefined &&
        !isNaN(lat) &&
        !isNaN(lon)
      ) {
        if (!isLatLonInBounds(lat, lon, config)) {
          continue;
        }

        let pixel = latLonToPixel(lat, lon, config, videoHeight);
        // console.log("pixel--------->", pixel);
        if (scalingInfo?.needsScaling) {
          pixel = { x: pixel.x, y: pixel.y };
        }

        if (isPointInBounds(pixel, config, videoHeight)) {
          trackPoints.push(pixel);
          validPoints.push({ pixel, index: i });
        }
      }
    }

    for (let i = 0; i < validPoints.length; i++) {
      const currentPoint = validPoints[i];

      if (i === 0) {
        connectingLinesData += `M ${currentPoint.pixel.x} ${currentPoint.pixel.y}`;
      } else {
        const prevPoint = validPoints[i - 1];
        if (currentPoint.index === prevPoint.index + 1) {
          connectingLinesData += ` L ${currentPoint.pixel.x} ${currentPoint.pixel.y}`;
        } else {
          connectingLinesData += ` M ${currentPoint.pixel.x} ${currentPoint.pixel.y}`;
        }
      }
    }

    return { trackPoints, connectingLines: connectingLinesData };
  };
  //get video frame click it will position of the video to show
  const getFrameBasedPosition = (
    frameIndex: number,
    forecastData: any,
    config: any,
    scalingInfo: ScalingInfo | null = null,
    videoHeight: number
  ): TrackPoint | null => {
    if (!forecastData?.lats || !forecastData?.lons) return null;

    const validPointsWithIndices: Array<{
      index: number;
      lat: number;
      lon: number;
      pixel: TrackPoint;
    }> = [];

    for (let i = 0; i < forecastData.lats.length; i++) {
      const lat = forecastData.lats[i];
      const lon = forecastData.lons[i];

      if (
        lat !== undefined &&
        lon !== undefined &&
        !isNaN(lat) &&
        !isNaN(lon)
      ) {
        if (isLatLonInBounds(lat, lon, config)) {
          let pixel = latLonToPixel(lat, lon, config, videoHeight);
          if (scalingInfo?.needsScaling) {
            pixel = { x: pixel.x, y: pixel.y };
          }

          if (isPointInBounds(pixel, config, videoHeight)) {
            validPointsWithIndices.push({ index: i, lat, lon, pixel });
          }
        }
      }
    }

    if (validPointsWithIndices.length === 0) return null;

    const maxValidIndex = validPointsWithIndices.length - 1;
    const normalizedFrameIndex = Math.min(frameIndex, maxValidIndex);

    const lowerIndex = Math.floor(normalizedFrameIndex);
    const upperIndex = Math.min(lowerIndex + 1, maxValidIndex);
    const t = normalizedFrameIndex - lowerIndex;

    const point1 = validPointsWithIndices[lowerIndex];

    if (lowerIndex === upperIndex || t === 0) {
      return point1.pixel;
    }

    const point2 = validPointsWithIndices[upperIndex];

    const lat = point1.lat + (point2.lat - point1.lat) * t;
    const lon = point1.lon + (point2.lon - point1.lon) * t;

    let pixelPos = latLonToPixel(lat, lon, config, videoHeight);
    if (scalingInfo?.needsScaling) {
      pixelPos = { x: pixelPos.x, y: pixelPos.y };
    }

    return pixelPos;
  };
  //to get video to load use blob using store and process
  const loadVideoOverlayData = async (): Promise<void> => {
    try {
      const configUrl = `${
        process.env.REACT_APP_BACKEND_IP
      }/api/video-config?region=${encodeURIComponent(selectedRegion)}`;
      const configResponse = await fetch(configUrl);

      if (!configResponse.ok) {
        console.warn(`Video config not found for region: ${selectedRegion}`);
        setInitTimeMatched(false);
        setVideoConfig(null);
        setTrackingData(null);
        return;
      }

      const regionConfig = await configResponse.json();

      if (!regionConfig || Object.keys(regionConfig).length === 0) {
        console.warn(`Empty video config for region: ${selectedRegion}`);
        setInitTimeMatched(false);
        setVideoConfig(null);
        setTrackingData(null);
        return;
      }

      setVideoConfig(regionConfig);

      const initTime = regionConfig.init_time;
      if (!initTime) {
        console.warn("No init_time in video config");
        setInitTimeMatched(false);
        setTrackingData(null);
        return;
      }

      const trackingUrl = `${
        process.env.REACT_APP_BACKEND_IP
      }/api/tracking-data?init_time=${encodeURIComponent(
        initTime
      )}&forecast_id=${localStorage.fid}`;
      const trackingResponse = await fetch(trackingUrl);

      if (!trackingResponse.ok) {
        console.warn(`Tracking data not found for init_time: ${initTime}`);
        setInitTimeMatched(false);
        setTrackingData(null);
        return;
      }

      const trackingData = await trackingResponse.json();

      if (trackingData && Object.keys(trackingData).length > 0) {
        setTrackingData(trackingData);
        setInitTimeMatched(true);
      } else {
        console.warn("Empty tracking data received");
        setTrackingData(null);
        setInitTimeMatched(false);
      }
    } catch (error) {
      console.error("Error loading overlay data:", error);
      setVideoConfig(null);
      setTrackingData(null);
      setInitTimeMatched(false);
    }
  };
  //no of video frame calculate
  const calculateVideoFrame = (): number => {
    if (!videoRef.current || !videoConfig) return 0;
    const currentTime = videoRef.current.currentTime;
    const totalDuration = videoRef.current.duration;
    if (!totalDuration) return 0;
    const totalFrames =
      videoConfig.corrected_frames || videoConfig.nr_of_frames;
    return Math.min(
      Math.max((currentTime / totalDuration) * (totalFrames - 1), 0),
      totalFrames - 1
    );
  };
  //move next next frame video show animation
  const updateAnimation = (): void => {
    if (videoRef.current && videoConfig && isPlaying) {
      const newFrame = calculateVideoFrame();
      if (Math.abs(newFrame - currentFrame) > 0.01) setCurrentFrame(newFrame);
    }
    if (isPlaying)
      animationRef.current = requestAnimationFrame(updateAnimation);
  };
  //to fetch image for indiviual
  const fetchIndividualImages = async () => {
    if (!selectedType || !selectedRegion) return;

    setLoadingImages(true);
    try {
      const response = await fetch(
        `${
          process.env.REACT_APP_BACKEND_IP
        }/api/images/?chart_type=${selectedType.toLowerCase()}&region=${encodeURIComponent(
          selectedRegion
        )}`
      );
      const data = await response.json();
      setImages(data.images || []);
      setCurrentImageIndex(0);
      setShowImagesPopup(true);
    } catch (error) {
      console.error("Error fetching images:", error);
      alert("Failed to load individual charts");
    } finally {
      setLoadingImages(false);
    }
  };

  const handleNextImage = () => {
    if (currentImageIndex < images.length - 1) {
      setCurrentImageIndex((prev) => prev + 1);
    }
  };

  const handlePrevImage = () => {
    if (currentImageIndex > 0) {
      setCurrentImageIndex((prev) => prev - 1);
    }
  };

  const handleClosePopup = () => {
    setShowImagesPopup(false);
    setImages([]);
    setCurrentImageIndex(0);
  };
  //vidoe full view esc retutn video file keyboard
  useEffect(() => {
    if (isFullView) {
      const handleKeyPress = (e: KeyboardEvent) => {
        if (e.key === "Escape") setIsFullView(false);
      };
      document.addEventListener("keydown", handleKeyPress);
      document.body.style.overflow = "hidden";
      return () => {
        document.removeEventListener("keydown", handleKeyPress);
        document.body.style.overflow = "unset";
      };
    }
  }, [isFullView]);
  //play video animation, next animation
  useEffect(() => {
    if (isPlaying) {
      animationRef.current = requestAnimationFrame(updateAnimation);
    } else if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [isPlaying, currentFrame, videoConfig]);
  //normal screen to full screen adjustment
  useEffect(() => {
    const handleFullscreenChange = (): void => {
      const isFullscreen = !!(
        document.fullscreenElement || (document as any).webkitFullscreenElement
      );
      setIsFullscreenMode(isFullscreen);
      setTimeout(() => setForceRender((prev) => prev + 1), 100);
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener(
        "webkitfullscreenchange",
        handleFullscreenChange
      );
    };
  }, []);

  useEffect(() => {
    if (selectedRegion) {
      loadVideoOverlayData();
    }
  }, [selectedRegion]);

  useEffect(() => {
    if (!isGeneratingThumbnails && videoRef.current) {
      const timer = setTimeout(() => setForceRender((prev) => prev + 1), 100);
      return () => clearTimeout(timer);
    }
  }, [isGeneratingThumbnails]);
  //nearest loation
  useEffect(() => {
    axios
      .get(
        `${process.env.REACT_APP_BACKEND_IP}/api/cyclone/${localStorage.fid}/`
      )
      .then((response) => {
        const data = response.data;
        if (
          data.project_location?.latitude &&
          data.project_location?.longitude
        ) {
          setProjectLocation({
            latitude: data.project_location.latitude,
            longitude: data.project_location.longitude,
          });

          return fetch(
            `${process.env.REACT_APP_BACKEND_IP}/api/nearest-region?latitude=${data.project_location.latitude}&longitude=${data.project_location.longitude}`
          );
        }
      })
      .then((res) => res?.json())
      .then((data) => {
        if (data?.nearest_region) {
          // console.log("Nearest region:", data.nearest_region.name);
          setSelectedRegion(data.nearest_region.name);
        }
        setLoadingLocation(false);
      })
      .catch((err) => {
        console.error("Error loading location:", err);
        setLoadingLocation(false);
      });
  }, []);
  //region select based timestamp show data
  useEffect(() => {
    if (!selectedType || !selectedRegion) return;

    loadAttemptRef.current += 1;
    const currentAttempt = loadAttemptRef.current;

    setLoadingFiles(true);
    setVideoError("");

    const videoUrl = `${
      process.env.REACT_APP_BACKEND_IP
    }/api/video/?chart_type=${selectedType.toLowerCase()}&region=${encodeURIComponent(
      selectedRegion
    )}&timestamp=${Date.now()}`;

    fetch(videoUrl)
      .then((res) => {
        if (!res.ok) {
          if (res.status === 404) {
            throw new Error("Video not found");
          }
          throw new Error(`HTTP ${res.status}: ${res.statusText}`);
        }
        return res.blob();
      })
      .then((blob) => {
        if (currentAttempt === loadAttemptRef.current) {
          const blobUrl = URL.createObjectURL(blob);
          setVideoUrl(blobUrl);
          setLoadingFiles(false);
        }
      })
      .catch((error) => {
        console.error("Error loading video:", error);
        if (currentAttempt === loadAttemptRef.current) {
          const errorMessage =
            error.message === "Video not found"
              ? "Video not found for the selected region and chart type"
              : `Failed to load video: ${error.message}`;
          setVideoError(errorMessage);
          setLoadingFiles(false);
        }
      });
  }, [selectedType, selectedRegion]);

  useEffect(() => {
    return () => {
      if (videoUrl && videoUrl.startsWith("blob:")) {
        URL.revokeObjectURL(videoUrl);
      }
    };
  }, [selectedType, selectedRegion]);

  const handleTypeChange = (event: SelectChangeEvent<string>): void => {
    setSelectedType(event.target.value);
  };
  // to play disble download button and fullscreen
  const renderVideoContent = (): JSX.Element => (
    <div>
      <div
        className={`video-container-wrapper ${isFullView ? "full-view" : ""}`}
      >
        <div className="video-wrapper">
          {videoReady && !videoError && (
            <button
              onClick={() => setIsFullView(!isFullView)}
              className="full-view-button"
            >
              {isFullView ? "Exit Full View" : "Full View"}
            </button>
          )}
          <video
            ref={videoRef}
            controls={videoReady && !videoError}
            autoPlay
            playsInline
            preload="auto"
            controlsList="nodownload nofullscreen"
            disablePictureInPicture={true}
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            onSeeked={() =>
              videoConfig && setCurrentFrame(calculateVideoFrame())
            }
            onLoadedMetadata={() => {
              setVideoReady(true);
              setTimeout(() => setForceRender((prev) => prev + 1), 100);
            }}
            onLoadedData={() => {
              setTimeout(() => setForceRender((prev) => prev + 1), 150);
            }}
            onError={(e) => {
              console.error("Video error:", e);
              setVideoError("Failed to load video");
            }}
            onContextMenu={(e) => e.preventDefault()}
            className={`video-element ${isFullView ? "full-view" : ""}`}
          >
            <source src={videoUrl || ""} type="video/mp4" />
          </video>
        </div>
        {/* initial time is macth, check track data and video config check video is error or not*/}
        {videoConfig &&
          videoRef.current &&
          !isFullscreenMode &&
          videoReady &&
          !videoError &&
          (() => {
            const video = videoRef.current;

            if (
              !video ||
              !video.videoWidth ||
              !video.videoHeight ||
              video.readyState < 2
            ) {
              return null;
            }

            const videoRect = video.getBoundingClientRect();
            const videoAspectRatio = video.videoWidth / video.videoHeight;
            const containerAspectRatio = videoRect.width / videoRect.height;

            let actualVideoWidth, actualVideoHeight, offsetX, offsetY;
            if (containerAspectRatio > videoAspectRatio) {
              actualVideoHeight = videoRect.height;
              actualVideoWidth = actualVideoHeight * videoAspectRatio;
              offsetX = (videoRect.width - actualVideoWidth) / 2;
              offsetY = 0;
            } else {
              actualVideoWidth = videoRect.width;
              actualVideoHeight = actualVideoWidth / videoAspectRatio;
              offsetX = 0;
              offsetY = (videoRect.height - actualVideoHeight) / 2;
            }

            let projectLocationPixel = null;
            let isProjectLocationInBounds = false;

            if (projectLocation) {
              // console.log("Project Location:", projectLocation);
              // console.log("Video Config Bounds:", {
              //   minLat: videoConfig.minlat,
              //   maxLat: videoConfig.maxlat,
              //   minLon: videoConfig.minlon,
              //   maxLon: videoConfig.maxlon,
              // });

              isProjectLocationInBounds = isLatLonInBounds(
                projectLocation.latitude,
                projectLocation.longitude,
                videoConfig
              );

              // console.log(
              //   "Project location in video bounds:",
              //   isProjectLocationInBounds
              // );

              if (isProjectLocationInBounds) {
                projectLocationPixel = latLonToPixel(
                  projectLocation.latitude,
                  projectLocation.longitude,
                  videoConfig,
                  video.videoHeight
                );

                // console.log("Project Location Pixel:", projectLocationPixel);

                const isPixelInBounds = isPointInBounds(
                  projectLocationPixel,
                  videoConfig,
                  video.videoHeight
                );

                // console.log("Pixel in plot bounds:", isPixelInBounds);

                if (!isPixelInBounds) {
                  projectLocationPixel = null;
                }
              }
            }

            let trackPaths: TrackPath = {
              trackPoints: [],
              connectingLines: "",
            };
            let markerPos = null;

            if (initTimeMatched && trackingData) {
              const forecastId = localStorage.fid;
              const forecastData = trackingData[forecastId];

              if (
                forecastData?.lats &&
                forecastData?.lons &&
                forecastData.lats.length > 0
              ) {
                const pixelPoints = forecastData.lats
                  .map((lat: number, idx: number) => {
                    const lon = forecastData.lons[idx];
                    return lat !== undefined &&
                      lon !== undefined &&
                      !isNaN(lat) &&
                      !isNaN(lon)
                      ? latLonToPixel(lat, lon, videoConfig, video.videoHeight)
                      : null;
                  })
                  .filter(
                    (p: TrackPoint | null): p is TrackPoint => p !== null
                  );

                let scalingInfo: ScalingInfo | null = null;
                if (pixelPoints.length > 1) {
                  const xCoords = pixelPoints.map((p: TrackPoint) => p.x);
                  const yCoords = pixelPoints.map((p: TrackPoint) => p.y);
                  const ranges = {
                    xMin: Math.min(...xCoords),
                    xMax: Math.max(...xCoords),
                    yMin: Math.min(...yCoords),
                    yMax: Math.max(...yCoords),
                  };

                  const mapWidth = videoConfig.plot_width_pix;
                  const mapHeight = videoConfig.plot_height_pix;

                  if (
                    ranges.yMin < 0 ||
                    ranges.yMax > mapHeight ||
                    ranges.xMin < 0 ||
                    ranges.xMax > mapWidth
                  ) {
                    const xRange = ranges.xMax - ranges.xMin;
                    const yRange = ranges.yMax - ranges.yMin;
                    const scale = Math.min(
                      xRange > 0 ? (mapWidth * 0.8) / xRange : 1,
                      yRange > 0 ? (mapHeight * 0.8) / yRange : 1
                    );
                    scalingInfo = {
                      needsScaling: true,
                      scale,
                      offsetX:
                        (mapWidth - xRange * scale) / 2 - ranges.xMin * scale,
                      offsetY:
                        (mapHeight - yRange * scale) / 2 - ranges.yMin * scale,
                    };
                  }
                }

                trackPaths = createTrackPath(
                  forecastData,
                  videoConfig,
                  scalingInfo,
                  video.videoHeight
                );

                markerPos = getFrameBasedPosition(
                  currentFrame,
                  forecastData,
                  videoConfig,
                  scalingInfo,
                  video.videoHeight
                );
              }
            }
            /* Path plotting, lat lon circle plotting */
            return (
              <svg
                key={`overlay-${forceRender}`}
                className="svg-overlay"
                style={{
                  top: `${offsetY}px`,
                  left: `${offsetX}px`,
                  width: `${actualVideoWidth}px`,
                  height: `${actualVideoHeight}px`,
                }}
                viewBox={`0 0 ${video.videoWidth} ${video.videoHeight}`}
                preserveAspectRatio="none"
              >
                {markerPos &&
                  isPointInBounds(
                    markerPos,
                    videoConfig,
                    video.videoHeight
                  ) && (
                    <image
                      href={markerImage}
                      x={markerPos.x - 30}
                      y={markerPos.y - 30}
                      width="70"
                      height="70"
                      opacity="1.0"
                    />
                  )}

                <path
                  d={trackPaths.connectingLines}
                  stroke="red"
                  strokeWidth="2"
                  strokeDasharray="2,2"
                  fill="none"
                  opacity="0.9"
                />
                {trackPaths.trackPoints.map(
                  (point: TrackPoint, idx: number) => (
                    <circle
                      key={idx}
                      cx={point.x}
                      cy={point.y}
                      r="5"
                      fill="blue"
                      stroke="white"
                      strokeWidth="1"
                      opacity="0.9"
                    />
                  )
                )}

                {projectLocationPixel && (
                  <g>
                    <circle
                      cx={projectLocationPixel.x}
                      cy={projectLocationPixel.y}
                      r="15"
                      fill="green"
                      opacity="0.3"
                    >
                      <animate
                        attributeName="r"
                        values="15;20;15"
                        dur="2s"
                        repeatCount="indefinite"
                      />
                      <animate
                        attributeName="opacity"
                        values="0.3;0.1;0.3"
                        dur="2s"
                        repeatCount="indefinite"
                      />
                    </circle>

                    <circle
                      cx={projectLocationPixel.x}
                      cy={projectLocationPixel.y}
                      r="8"
                      fill="green"
                      stroke="white"
                      strokeWidth="2"
                      opacity="1.0"
                    />

                    <circle
                      cx={projectLocationPixel.x}
                      cy={projectLocationPixel.y}
                      r="3"
                      fill="white"
                      opacity="1.0"
                    />

                    <text
                      x={projectLocationPixel.x}
                      y={projectLocationPixel.y - 20}
                      fill="white"
                      fontSize="12"
                      fontWeight="bold"
                      textAnchor="middle"
                      stroke="black"
                      strokeWidth="3"
                      paintOrder="stroke"
                    >
                      {/* Project Location */}
                      {/* let project = localStorage.getItem("project");
                  let trimmedProject = project
                    ? project.match(/^(.*?)(?=-\d+)/)?.[0].trim() || ""
                    : ""; */}
                      {trimmedProject}
                    </text>
                  </g>
                )}
              </svg>
            );
          })()}
      </div>
      {initTimeMatched && !isFullscreenMode && !isFullView && (
        <CustomTimeline
          trackingData={trackingData}
          videoConfig={videoConfig}
          currentFrame={currentFrame}
          onFrameClick={(frame) => setCurrentFrame(frame)}
          videoRef={videoRef}
          setIsGeneratingThumbnails={setIsGeneratingThumbnails}
        />
      )}
    </div>
  );

  return (
    <div className="chart">
      {loadingLocation ? (
        <div className="loader"></div>
      ) : (
        <>
          <div className="header">
            <h1>Wind - Waves - Current Charts</h1>
          </div>
          <div className="controls">
            <FormControl className="dropdown">
              <InputLabel id="chart-type-select-label">
                Select Chart Type
              </InputLabel>
              <Select
                labelId="chart-type-select-label"
                id="chart-type-select"
                value={selectedType}
                label="Select Chart Type"
                onChange={handleTypeChange}
              >
                {["Wind", "Waves", "Currents"].map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant="contained"
              color="primary"
              onClick={fetchIndividualImages}
              disabled={!selectedType || !selectedRegion || loadingImages}
            >
              {loadingImages ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Individual Charts"
              )}
            </Button>
          </div>

          <div className="image-card">
            {loadingFiles ? (
              <div className="loading-container">
                <CircularProgress size={50} />
                <div className="loading-text">Loading video...</div>
              </div>
            ) : videoError ? (
              <div className="error-container">
                <div>{videoError}</div>
                <button
                  onClick={() => {
                    setVideoError("");
                    setLoadingFiles(true);
                    loadAttemptRef.current += 1;
                    const currentAttempt = loadAttemptRef.current;

                    const videoUrl = `${
                      process.env.REACT_APP_BACKEND_IP
                    }/api/video/?chart_type=${selectedType.toLowerCase()}&region=${encodeURIComponent(
                      selectedRegion
                    )}&timestamp=${Date.now()}`;

                    fetch(videoUrl)
                      .then((res) => {
                        if (!res.ok) {
                          if (res.status === 404) {
                            throw new Error("Video not found");
                          }
                          throw new Error(
                            `HTTP ${res.status}: ${res.statusText}`
                          );
                        }
                        return res.blob();
                      })
                      .then((blob) => {
                        if (currentAttempt === loadAttemptRef.current) {
                          const blobUrl = URL.createObjectURL(blob);
                          setVideoUrl(blobUrl);
                          setLoadingFiles(false);
                        }
                      })
                      .catch((error) => {
                        if (currentAttempt === loadAttemptRef.current) {
                          const errorMessage =
                            error.message === "Video not found"
                              ? "Video not found for the selected region and chart type"
                              : `Failed to load video: ${error.message}`;
                          setVideoError(errorMessage);
                          setLoadingFiles(false);
                        }
                      });
                  }}
                  className="retry-button"
                >
                  Retry
                </button>
              </div>
            ) : videoUrl ? (
              renderVideoContent()
            ) : (
              <div className="empty-state">
                Select a chart type to load video
              </div>
            )}
          </div>

          <Dialog
            open={showImagesPopup}
            onClose={handleClosePopup}
            maxWidth="lg"
            fullWidth
            PaperProps={{
              className: "dialog-paper",
            }}
          >
            <DialogTitle className="dialog-title">
              <span style={{ color: "white" }}>
                Individual Charts - {selectedType}
              </span>
              <IconButton
                edge="end"
                color="inherit"
                onClick={handleClosePopup}
                aria-label="close"
                sx={{ color: "white" }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent className="dialog-content">
              {images.length > 0 ? (
                <div className="image-navigation-wrapper">
                  {currentImageIndex > 0 && (
                    <IconButton
                      onClick={handlePrevImage}
                      className="nav-button"
                    >
                      <ArrowCircleLeftIcon />
                    </IconButton>
                  )}
                  <div className="image-display-container">
                    <img
                      src={`data:image/png;base64,${images[currentImageIndex].data}`}
                      alt={images[currentImageIndex].title}
                    />
                    {/* <div className="image-title">
                      {images[currentImageIndex].title}
                    </div> */}
                    <div className="image-counter">
                      Image {currentImageIndex + 1} of {images.length}
                    </div>
                  </div>
                  {currentImageIndex < images.length - 1 && (
                    <IconButton
                      onClick={handleNextImage}
                      className="nav-button"
                    >
                      <ArrowCircleRightIcon />
                    </IconButton>
                  )}
                </div>
              ) : (
                <div className="dialog-loading">
                  <CircularProgress size={50} />
                  <div>Loading images...</div>
                </div>
              )}
            </DialogContent>
          </Dialog>
        </>
      )}
    </div>
  );
};

export default Charts;
